import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Phone, PhoneCall, MessageCircle, Send, ArrowRightLeft, Save, User, Settings, RefreshCw, PhoneOff, Loader2, ChevronLeft, ChevronRight, MapPin, Copy, Check, Image, Upload, X, Trash2, FileText, Calendar, Clock, History, CheckCircle, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../hooks/useAuth";
import { createLeadComment, deleteLeadComment, updateLead, uploadScreenshots, deleteScreenshot, getLeadDataById, fetchDistinctValues } from "../../services/crmServices/leadService";
import { getFullName } from "../../services/common/util";
import { serviceConstants } from "../../services/common/constants";
import { format } from "date-fns";
import moment from "moment-timezone";
import { useSelector } from "react-redux";
import { selectLeadStatuses } from "../../store/slices/leadStatusSlice";
import { selectAllLeads } from "../../store/slices/leadsSlice";
import { AgentTransferModal } from "../transfers/AgentTransferModal";
import { fetchUsers } from "../../services/crmServices/userService";
import {
    initiateVoisoCall,
    initiateCommpeakCall,
    initiateCoperatoCall,
    initiateSquareTalkCall,
    initiateGlobalCallCall,
    initiateBubbleVoipCall,
    getSquareTalkCallStatus,
    handleCallCompletion,
    endCall
} from "../../services/voipServices";
import { createLog } from "../../services/crmServices/logService";
import { CallbackScheduleDialog } from "../callbacks/CallbackScheduleDialog";
import { scheduleCallback, rescheduleCallback, completeCallback, cancelCallback } from "../../api/callbackApi";

export function VoiceCallModal({ open, onClose, lead, onSuccess, onLeadUpdate, filteredLeads = [], currentLeadIndex = -1, onNavigate, totalLeads = 0, currentPage = 1, pageSize = 100, onPageChange }) {
    const { user } = useAuth();
    const leadStatuses = useSelector(selectLeadStatuses);
    const allLeadsFromRedux = useSelector(selectAllLeads); // Get all leads from Redux to access updated data
    const [localLead, setLocalLead] = useState(lead);
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        status: "new",
        source: ""
    });
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState("");
    const [selectedProvider, setSelectedProvider] = useState("");
    const [loading, setLoading] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [showCallbackDialog, setShowCallbackDialog] = useState(false);
    const [callbackMode, setCallbackMode] = useState("schedule");
    const [users, setUsers] = useState([]);
    const [activeCall, setActiveCall] = useState(null);
    const [endingCall, setEndingCall] = useState(false);
    const pollingIntervalRef = useRef(null);
    const [localTime, setLocalTime] = useState("");
    const [emailCopied, setEmailCopied] = useState(false);
    const [screenshots, setScreenshots] = useState([]);
    const [uploadingScreenshots, setUploadingScreenshots] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const fileInputRef = useRef(null);
    const [sourceOptions, setSourceOptions] = useState([]);
    
    const [addingComment, setAddingComment] = useState(false);
    const lastCommentClickRef = useRef(0);
    const [navigationLoading, setNavigationLoading] = useState(false);
    const lastNavigationClickRef = useRef(0);
    const lastCheckedLeadIdRef = useRef(null);
    const leadIdRef = useRef(lead?._id);
    const isSavingRef = useRef(false);
    
    // Sync localLead with prop changes, but only when lead ID actually changes
    // This prevents re-syncing when parent re-renders with same lead data
    useEffect(() => {
        // Only sync if the lead ID has changed (new lead) or if localLead is null
        if (!localLead || (lead?._id && lead._id !== leadIdRef.current)) {
            leadIdRef.current = lead?._id;
            setLocalLead(lead);
        }
    }, [lead?._id]); // Only depend on lead ID, not the entire lead object
    
    // Prevent Dialog from closing during save operations
    const handleDialogOpenChange = (newOpen) => {
        // Don't allow closing if we're currently saving
        if (!newOpen && isSavingRef.current) {
            // Prevent closing during save - do nothing
            return;
        }
        // Only call onClose if we're actually closing (newOpen is false)
        if (!newOpen) {
            onClose();
        }
    };
    
    // Note: We intentionally DON'T auto-navigate away when a lead is filtered out
    // The lead should remain in the modal so the agent can continue working on it
    // The lead will be updated via onLeadUpdate when saved, keeping it in sync
    
    // Navigation helpers - simple approach: only navigate within current page
    const hasNavigation = currentLeadIndex >= 0 && filteredLeads.length > 0;
    const indexInPage = currentLeadIndex % pageSize;
    
    // Always allow navigation (will refresh and restart when reaching page boundaries)
    const canGoNext = hasNavigation;
    const canGoPrevious = hasNavigation;
    
    const totalLeadsCount = totalLeads || filteredLeads.length;
    const leadPosition = hasNavigation ? `Lead ${currentLeadIndex + 1} of ${totalLeadsCount.toLocaleString()}` : null;
    
    // Get timezone from country (simple mapping for common countries)
    const getTimezoneFromCountry = (country) => {
        const countryTimezones = {
            'United States': 'America/New_York',
            'United Kingdom': 'Europe/London',
            'Germany': 'Europe/Berlin',
            'France': 'Europe/Paris',
            'Spain': 'Europe/Madrid',
            'Italy': 'Europe/Rome',
            'Canada': 'America/Toronto',
            'Australia': 'Australia/Sydney',
            'Japan': 'Asia/Tokyo',
            'China': 'Asia/Shanghai',
            'India': 'Asia/Kolkata',
            'Brazil': 'America/Sao_Paulo',
            'Mexico': 'America/Mexico_City',
            'Netherlands': 'Europe/Amsterdam',
            'Sweden': 'Europe/Stockholm',
            'Norway': 'Europe/Oslo',
            'Denmark': 'Europe/Copenhagen',
            'Poland': 'Europe/Warsaw',
            'UAE': 'Asia/Dubai',
            'Saudi Arabia': 'Asia/Riyadh',
            'South Africa': 'Africa/Johannesburg',
            'Singapore': 'Asia/Singapore',
            'Malaysia': 'Asia/Kuala_Lumpur',
            'Thailand': 'Asia/Bangkok',
            'Philippines': 'Asia/Manila',
            'Indonesia': 'Asia/Jakarta',
            'Turkey': 'Europe/Istanbul',
            'Russia': 'Europe/Moscow',
            'Israel': 'Asia/Jerusalem',
            'Egypt': 'Africa/Cairo'
        };
        return countryTimezones[country] || 'UTC';
    };
    
    // Update local time every second
    useEffect(() => {
        if (!localLead?.country) {
            setLocalTime("");
            return;
        }
        
        const updateTime = () => {
            const timezone = getTimezoneFromCountry(localLead.country);
            const time = moment().tz(timezone).format('HH:mm:ss');
            const date = moment().tz(timezone).format('MMM DD, YYYY');
            setLocalTime(`${time} (${date})`);
        };
        
        updateTime();
        const interval = setInterval(updateTime, 1000);
        return () => clearInterval(interval);
    }, [localLead?.country]);

    // Keyboard shortcuts for status selection (only when modal is open)
    useEffect(() => {
        if (!open || !user.permissions.editLeadFields?.status) return;

        const handleKeyPress = (e) => {
            // Ignore if user is typing in an input, textarea, or select
            const target = e.target;
            if (
                target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.tagName === 'SELECT' ||
                target.isContentEditable
            ) {
                return;
            }

            // Get the pressed key in uppercase
            const key = e.key.toUpperCase();

            // Find matching status by keyboard shortcut (case-insensitive)
            const matchingStatus = leadStatuses.find(
                (status) => status.keyboardShortcut && status.keyboardShortcut.toUpperCase() === key
            );

            if (matchingStatus) {
                // Prevent default behavior
                e.preventDefault();
                
                // Update status
                setFormData((prev) => ({
                    ...prev,
                    status: matchingStatus.value
                }));
                
                // Show feedback toast
                toast.success(`Status changed to: ${matchingStatus.label}`, {
                    duration: 1500
                });
            }
        };

        // Add event listener
        window.addEventListener('keydown', handleKeyPress);

        // Cleanup
        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [open, leadStatuses, user.permissions.editLeadFields?.status]);

    // Initialize form data when lead changes
    useEffect(() => {
        if (localLead) {
            // Fetch full lead data to ensure comments.author is populated
            // This is needed because leads from getAvailable() don't have populated comment authors
            const fetchFullLeadData = async () => {
                try {
                    const response = await getLeadDataById(localLead._id);
                    if (response?.data?._id) {
                        setLocalLead(response.data);
                        
                        // Update comments with sorted, properly structured data
                        if (response.data.comments && response.data.comments.length) {
                            const sortedComments = response.data.comments
                                .map(comment => ({
                                    ...comment,
                                    id: comment._id || comment.id || Math.random().toString(36).substr(2, 9),
                                    isSystem: comment.isSystem || false
                                }))
                                .sort((a, b) => {
                                    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                                    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                                    return dateB - dateA; // Descending order (newest first)
                                });
                            setComments(sortedComments);
                        }
                    }
                } catch (error) {
                    console.error("Error fetching full lead data:", error);
                    // Continue with existing lead data if fetch fails
                }
            };
            
            // Only fetch if comments exist and author is not populated (ObjectId instead of object)
            const hasUnpopulatedAuthors = localLead.comments?.some(
                comment => comment.author && typeof comment.author === 'string'
            );
            
            if (hasUnpopulatedAuthors) {
                fetchFullLeadData();
            }
            
            setFormData({
                firstName: localLead.firstName || "",
                lastName: localLead.lastName || "",
                phone: localLead.phone || "",
                email: localLead.email || "",
                status: localLead.status || "new",
                source: localLead.source || ""
            });
            // Sort comments by createdAt descending (newest first) and ensure proper structure
            const sortedComments = (localLead.comments || [])
                .map(comment => ({
                    ...comment,
                    id: comment._id || comment.id || Math.random().toString(36).substr(2, 9),
                    isSystem: comment.isSystem || false
                }))
                .sort((a, b) => {
                    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                    return dateB - dateA; // Descending order (newest first)
                });
            setComments(sortedComments);
            
            // Load screenshots
            if (localLead.screenshots && Array.isArray(localLead.screenshots)) {
                setScreenshots(localLead.screenshots);
            } else {
                setScreenshots([]);
            }
        }
        fetchUsers().then((res) => {
            setUsers(res.data);
        });
    }, [localLead?._id]); // Only re-initialize when lead ID changes, not on every lead update

    // Fetch source options when modal opens
    useEffect(() => {
        const loadSources = async () => {
            try {
                const response = await fetchDistinctValues('source');
                if (response?.data) {
                    setSourceOptions(response.data.map(source => ({ 
                        label: source, 
                        value: source 
                    })));
                }
            } catch (error) {
                console.error("Error fetching sources:", error);
            }
        };
        
        if (open) {
            loadSources();
        }
    }, [open]);

    // Store activeCall in ref to avoid closure issues
    const activeCallRef = useRef(activeCall);
    useEffect(() => {
        activeCallRef.current = activeCall;
    }, [activeCall]);

    // Automatic call status polling and completion detection
    useEffect(() => {
        // Clear any existing polling interval
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }

        // Only poll for SquareTalk, skip CommPeak and other providers
        if (!activeCall || !activeCall.id || !lead?._id || activeCall.provider === "commpeak") {
            return;
        }

        const maxPollAttempts = 120; // Poll for up to 10 minutes (120 * 5 seconds)
        let pollAttempts = 0;

        const checkCallStatus = async () => {
            // Check current activeCall from ref (to avoid closure issues)
            const currentActiveCall = activeCallRef.current;
            
            // Stop if activeCall was cleared or max attempts reached
            if (!currentActiveCall || !currentActiveCall.id || pollAttempts >= maxPollAttempts) {
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
                return;
            }

            pollAttempts++;

            try {
                let statusResponse;
                
                // Get call status based on provider
                switch (currentActiveCall.provider) {
                    case "squaretalk":
                        statusResponse = await getSquareTalkCallStatus(currentActiveCall.id);
                        break;
                    // Add other providers as needed
                    default:
                        return;
                }

                if (statusResponse?.success && statusResponse?.data) {
                    const callData = statusResponse.data;
                    // SquareTalk API returns status in different possible fields
                    const callStatus = callData.status || callData.state || callData.call_status || callData.state;

                    // Check if call is completed
                    const completedStatuses = ["completed", "finished", "ended", "terminated", "closed", "success"];
                    const failedStatuses = ["failed", "busy", "no-answer", "rejected", "cancelled", "error"];

                    if (completedStatuses.includes(callStatus?.toLowerCase()) || 
                        failedStatuses.includes(callStatus?.toLowerCase())) {
                        
                        // Double-check activeCall still exists before processing completion
                        const finalActiveCall = activeCallRef.current;
                        if (!finalActiveCall) {
                            // Call was cleared, stop polling
                            if (pollingIntervalRef.current) {
                                clearInterval(pollingIntervalRef.current);
                                pollingIntervalRef.current = null;
                            }
                            return;
                        }

                        // Calculate duration
                        const endTime = new Date();
                        const startTime = finalActiveCall.startTime ? new Date(finalActiveCall.startTime) : new Date();
                        const duration = Math.floor((endTime - startTime) / 1000); // Duration in seconds

                        // Stop polling
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current);
                            pollingIntervalRef.current = null;
                        }

                        // Handle call completion
                        try {
                            await handleCallCompletion({
                                provider: finalActiveCall.provider,
                                callId: finalActiveCall.id,
                                leadId: lead._id,
                                agentId: user._id,
                                duration: duration,
                                status: callStatus,
                                startTime: startTime.toISOString()
                            });

                            // Clear active call only if it hasn't been cleared already
                            if (activeCallRef.current) {
                                setActiveCall(null);
                                setSelectedProvider("");
                            }

                            // Show success message
                            const minutes = Math.floor(duration / 60);
                            const seconds = duration % 60;
                            toast.success(`Call completed. Duration: ${minutes > 0 ? `${minutes}m ` : ""}${seconds}s`);
                            
                            // Don't call onSuccess - it causes page refresh
                            // Call completion was already handled above
                        } catch (error) {
                            toast.error("Call completed but failed to update lead");
                        }
                    }
                }
            } catch (error) {
                // Don't stop polling on error, might be temporary
            }
        };

        // Start polling every 5 seconds
        pollingIntervalRef.current = setInterval(checkCallStatus, 5000);

        // Also check immediately
        checkCallStatus();

        // Cleanup on unmount or when activeCall changes
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, [activeCall, lead?._id, user._id, onSuccess]);

    // Get available VOIP providers
    const getVoipProviders = () => {
        const providers = [];
        if (user.voiso && user.permissions.voip?.voiso) {
            providers.push({
                id: "voiso",
                name: "Voiso",
                color: "bg-indigo-600 hover:bg-indigo-700 text-white",
                icon: PhoneCall
            });
        }
        if (user.coperato && user.permissions.voip?.coperato) {
            providers.push({
                id: "coperato",
                name: "Coperato",
                color: "bg-violet-600 hover:bg-violet-700 text-white",
                icon: PhoneCall
            });
        }
        if (user.commpeak && user.permissions.voip?.commpeak) {
            providers.push({
                id: "commpeak",
                name: "CommPeak",
                color: "bg-emerald-600 hover:bg-emerald-700 text-white",
                icon: Phone
            });
        }
        if (user.squaretalk && user.permissions.voip?.squaretalk) {
            providers.push({
                id: "squaretalk",
                name: "SquareTalk",
                color: "bg-amber-600 hover:bg-amber-700 text-white",
                icon: PhoneCall
            });
        }
        if (user.sipAccount && user.permissions.voip?.globalcall) {
            providers.push({
                id: "globalcall",
                name: "GlobalCall",
                color: "bg-teal-600 hover:bg-teal-700",
                icon: PhoneCall
            });
        }
        if (user.bubblevoip && user.permissions.voip?.bubblevoip) {
            providers.push({
                id: "bubblevoip",
                name: "Bubble VoIP",
                color: "bg-indigo-600 hover:bg-indigo-700",
                icon: PhoneCall
            });
        }
        return providers;
    };

    const handleCall = async (provider) => {
        if (!formData.phone) {
            toast.error("Phone number is required to make a call");
            return;
        }

        setLoading(true);
        setSelectedProvider(provider.id);

        // For CommPeak, set activeCall immediately so refresh button appears right away
        if (provider.id === "commpeak") {
            setActiveCall({
                id: user.commpeak || `commpeak_${Date.now()}`,
                provider: provider.id,
                startTime: new Date(),
                status: "initiating",
                extension: user.commpeak
            });
        }

        try {
            const callParams = {
                phoneNumber: formData.phone,
                agentId: user._id,
                leadId: lead._id
            };

            // Fire-and-forget for CommPeak (instant feedback, no waiting)
            if (provider.id === "commpeak") {
                // Send API call in background without waiting
                initiateCommpeakCall(callParams).catch(err => {
                    console.error("CommPeak call error (background):", err);
                    // Don't show error to user - they already got success message
                });
                
                // Immediately update UI and create log
                setActiveCall({
                    id: user.commpeak || `commpeak_${Date.now()}`,
                    provider: provider.id,
                    startTime: new Date(),
                    status: "calling", // Set to calling immediately
                    extension: user.commpeak
                });
                
                toast.success("📞 CommPeak call initiated! Your phone should ring shortly.");
                
                // Create call log immediately
                createLog({
                    logType: "call",
                    call: {
                        lead: lead._id,
                        staff: user._id,
                        provider: provider.id,
                        callId: user.commpeak || `commpeak_${Date.now()}`,
                        startTime: new Date()
                    }
                });
                
                setLoading(false);
                return; // Exit early for CommPeak
            }

            // For other providers, wait for response as before
            let callResult;
            
            // Initiate call based on provider
            switch (provider.id) {
                case "voiso":
                    callResult = await initiateVoisoCall(callParams);
                    break;
                case "coperato":
                    callResult = await initiateCoperatoCall(callParams);
                    break;
                case "squaretalk":
                    callResult = await initiateSquareTalkCall(callParams);
                    break;
                case "globalcall":
                    callResult = await initiateGlobalCallCall({
                        phoneNumber: localLead.phone,
                        agentId: user._id,
                        leadId: localLead._id,
                        agentSipAccount: user.sipAccount,
                        clientName: getFullName(localLead.firstName, localLead.lastName)
                    });
                    break;
                case "bubblevoip":
                    callResult = await initiateBubbleVoipCall(callParams);
                    break;
                default:
                    toast.error("Provider not supported");
                    setLoading(false);
                    return;
            }
            if (callResult.success) {
                // GlobalCall & Bubble VoIP: Just notify and log, no call state management
                if (provider.id === "globalcall" || provider.id === "bubblevoip") {
                    const providerName = provider.id === "globalcall" ? "GlobalCall" : "Bubble VoIP";
                    const description = provider.id === "globalcall" 
                        ? "The call has been sent to your phone. Answer your phone to connect."
                        : "Your Zoiper should ring now. Answer to connect to the destination.";
                    
                    toast.success("Call Initiated", {
                        description: description
                    });
                    
                    // Log the call
                    createLog({
                        logType: "call",
                        call: {
                            lead: lead._id,
                            staff: user._id,
                            provider: provider.id,
                            callId: null,
                            startTime: new Date()
                        }
                    });
                    
                    // Clear provider selection so user can make another call
                    setSelectedProvider("");
                    return;
                }
                
                // Extract callId from response (may be in callId, data.call_id, or data.id)
                const callId = callResult.callId || callResult.data?.call_id || callResult.data?.id || callResult.data?.callId;
                
                // For SquareTalk, also store the extension for ending the call
                const extension = callResult.extension || callResult.data?.meta?.extension || user.squaretalk;
                
                if (!callId && provider.id !== "squaretalk" && provider.id !== "commpeak") {
                    toast.warning("Call initiated but call ID not found. Status polling may not work.");
                }
                
                // Update activeCall (or set it if not already set for CommPeak)
                setActiveCall({
                    id: callId || extension || user[provider.id] || `commpeak_${Date.now()}`, // For SquareTalk/CommPeak, use extension as id
                    provider: provider.id,
                    startTime: new Date(),
                    status: "initiated",
                    extension: extension || user[provider.id] // Store extension for SquareTalk/CommPeak end call
                });

                toast.success("Call initiated successfully");
                // Add call log with provider information
                createLog({
                    logType: "call",
                    call: {
                        lead: lead._id,
                        staff: user._id,
                        provider: provider.id, // Include provider so calls can be counted by provider
                        callId: callId || extension || user[provider.id] || null,
                        startTime: new Date()
                    }
                });
            } else {
                // Clear activeCall if call initiation failed
                if (provider.id === "commpeak") {
                    setActiveCall(null);
                }
                toast.error(`Failed to initiate call. Check your VOIP provider settings or try again later.`);
            }
        } catch (error) {
            // Clear activeCall if error occurred
            if (provider.id === "commpeak") {
                setActiveCall(null);
            }
            toast.error("Failed to initiate call. Check your VOIP provider settings or try again later.");
        } finally {
            setLoading(false);
        }
    };

    // Handle refresh call state
    const handleRefreshCall = () => {
        // Stop any polling that might be running
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
        
        // Clear all call-related state and refs
        activeCallRef.current = null;
        setActiveCall(null);
        setSelectedProvider("");
        setLoading(false); // Clear loading state to enable buttons immediately
        
        toast.success("Call state refreshed - you can make calls again");
    };

    // Handle end call
    const handleEndCall = async () => {
        if (!activeCall) {
            toast.error("No active call to end");
            return;
        }

        // CommPeak and GlobalCall don't support ending calls via API
        if (activeCall.provider === "commpeak" || activeCall.provider === "globalcall") {
            return;
        }

        setEndingCall(true);
        try {
            // For SquareTalk, use extension; for others, use callId
            const callIdentifier = activeCall.extension || activeCall.id;
            
            const result = await endCall(callIdentifier, activeCall.provider, activeCall.extension);
            
            if (result?.success) {
                toast.success("Call Ended", {
                    description: "The call has been terminated successfully"
                });
                
                // Calculate duration
                const endTime = new Date();
                const startTime = activeCall.startTime ? new Date(activeCall.startTime) : new Date();
                const duration = Math.floor((endTime - startTime) / 1000);
                
                // Handle call completion
                try {
                    await handleCallCompletion({
                        provider: activeCall.provider,
                        callId: activeCall.id,
                        leadId: lead._id,
                        agentId: user._id,
                        duration: duration,
                        status: "ended",
                        startTime: startTime.toISOString()
                    });
                } catch (error) {
                    // Error handling call completion - silent fail
                }
                
                // Clear active call
                setActiveCall(null);
                setSelectedProvider("");
                
                // Show duration
                const minutes = Math.floor(duration / 60);
                const seconds = duration % 60;
                toast.info(`Call ended. Duration: ${minutes > 0 ? `${minutes}m ` : ""}${seconds}s`);
                
                // Don't call onSuccess - it causes page refresh
                // Call was already logged via handleCallCompletion above
            } else {
                toast.error("Failed to End Call", {
                    description: result?.error || "Could not terminate the call"
                });
            }
        } catch (error) {
            toast.error("Failed to End Call", {
                description: error.message || "An error occurred while ending the call"
            });
        } finally {
            setEndingCall(false);
        }
    };

    const handleAddComment = async () => {
        if (newComment.trim()) {
            const comment = {
                leadId: lead._id,
                text: newComment.trim(),
                author: user._id,
                createdAt: moment().tz("America/New_York").toDate()
            };

            try {
                const response = await createLeadComment(comment);
                if (response?.status === 200) {
                    setNewComment("");
                    toast.success("Comment added successfully");
                    
                    // Refresh lead data to get populated comment author
                    await refreshLeadData();
                } else if (response.data?.RESULT === "ERROR") {
                    toast.error(response.data.message);
                } else {
                    toast.error(response.message || "Failed to add comment");
                }
            } catch (error) {
                console.error("Error adding comment:", error);
                toast.error(error.response?.data?.message || error.message || "Failed to add comment. Please try again.");
            }
        }
    };

    const handleDeleteComment = async (commentId) => {
        if (!window.confirm("Are you sure you want to delete this comment?")) {
            return;
        }

        try {
            const response = await deleteLeadComment({
                leadId: lead._id,
                commentId: commentId
            });

            if (response?.status === 200) {
                // Remove comment from local state
                setComments((prev) => prev.filter((comment) => {
                    const id = comment._id || comment.id;
                    return id !== commentId;
                }));
                toast.success("Comment deleted successfully");
                
                // Refresh lead data to get updated comments
                await refreshLeadData();
            } else {
                toast.error(response.message || "Failed to delete comment");
            }
        } catch (error) {
            console.error("Error deleting comment:", error);
            toast.error(error.response?.data?.message || error.message || "Failed to delete comment. Please try again.");
        }
    };

    const handleDeleteLastComment = async () => {
        if (!window.confirm("Are you sure you want to delete this comment?")) {
            return;
        }

        if (!lead?._id) {
            toast.error("Cannot delete comment: Lead ID is missing");
            return;
        }

        try {
            const response = await updateLead({ 
                _id: lead._id, 
                lastComment: "" 
            });

            if (response?.status === 200) {
                toast.success("Comment deleted successfully");
                
                // Refresh lead data to get updated lastComment
                await refreshLeadData();
            } else {
                toast.error(response.message || "Failed to delete comment");
            }
        } catch (error) {
            console.error("Error deleting last comment:", error);
            toast.error(error.response?.data?.message || error.message || "Failed to delete comment. Please try again.");
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleAddComment();
        }
    };

    // Navigation handlers with debounce and error handling
    const handlePrevious = async () => {
        // Debounce: prevent rapid clicks within 500ms
        const now = Date.now();
        if (now - lastNavigationClickRef.current < 500) {
            return;
        }
        lastNavigationClickRef.current = now;

        if (!canGoPrevious || !onNavigate || navigationLoading || loading) return;
        
        setNavigationLoading(true);
        
        try {
            const newIndex = currentLeadIndex - 1;
            const indexInPage = newIndex % pageSize;
            
            // Check if at the beginning of current page
            if (indexInPage < 0 || newIndex < 0) {
                // Reached beginning - refresh leads and restart from first
                toast.info("Finished calling current page. Refreshing leads...");
                
                // Trigger refresh
                if (onSuccess) {
                    onSuccess();
                }
                
                // Wait for refresh, then navigate to first lead
                setTimeout(() => {
                    // Go to page 1
                    if (onPageChange) {
                        onPageChange(1);
                    }
                    // Select first lead
                    if (filteredLeads.length > 0) {
                        onNavigate(filteredLeads[0], 0);
                    }
                }, 500);
                
                setNavigationLoading(false);
                return;
            }
            
            // Same page navigation
            if (indexInPage >= 0 && indexInPage < filteredLeads.length) {
                const targetLead = filteredLeads[indexInPage];
                if (targetLead) {
                    // Check Redux for latest version
                    const updatedLeadInRedux = allLeadsFromRedux.find(l => 
                        l._id?.toString() === targetLead._id?.toString()
                    );
                    onNavigate(updatedLeadInRedux || targetLead, newIndex);
                }
            }
        } catch (error) {
            console.error("Error navigating to previous lead:", error);
            toast.error("Failed to navigate. Please try again.");
        } finally {
            setNavigationLoading(false);
        }
    };

    const handleNext = async () => {
        // Debounce: prevent rapid clicks within 500ms
        const now = Date.now();
        if (now - lastNavigationClickRef.current < 500) {
            return;
        }
        lastNavigationClickRef.current = now;

        if (!canGoNext || !onNavigate || navigationLoading || loading) return;
        
        setNavigationLoading(true);
        
        try {
            const newIndex = currentLeadIndex + 1;
            const indexInPage = newIndex % pageSize;
            
            // Check if at the end of current page
            if (indexInPage >= filteredLeads.length || indexInPage === 0) {
                // Reached end of page - refresh leads and restart from first
                toast.info("Finished calling current page. Refreshing leads...");
                
                // Trigger refresh
                if (onSuccess) {
                    onSuccess();
                }
                
                // Wait for refresh, then navigate to first lead
                setTimeout(() => {
                    // Go to page 1
                    if (onPageChange) {
                        onPageChange(1);
                    }
                    // Select first lead
                    if (filteredLeads.length > 0) {
                        onNavigate(filteredLeads[0], 0);
                    }
                }, 500);
                
                setNavigationLoading(false);
                return;
            }
            
            // Same page navigation
            if (indexInPage >= 0 && indexInPage < filteredLeads.length) {
                const targetLead = filteredLeads[indexInPage];
                if (targetLead) {
                    // Check Redux for latest version
                    const updatedLeadInRedux = allLeadsFromRedux.find(l => 
                        l._id?.toString() === targetLead._id?.toString()
                    );
                    onNavigate(updatedLeadInRedux || targetLead, newIndex);
                }
            }
        } catch (error) {
            console.error("Error navigating to next lead:", error);
            toast.error("Failed to navigate. Please try again.");
        } finally {
            setNavigationLoading(false);
        }
    };

    const handleSave = async () => {
        setLoading(true);
        isSavingRef.current = true;
        try {
            // Only send changed fields to avoid overwriting desk/agent with undefined
            const updateData = {
                _id: lead._id,
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formData.phone,
                email: formData.email,
                status: formData.status,
                source: formData.source,
                comments: comments
            };

            const response = await updateLead(updateData);
            if (response.status === 200) {
                toast.success("Lead updated successfully");
                // Update lead in Redux dynamically without refresh
                // API returns: { status: 200, data: { RESULT: "SUCCESS", lead: lead } }
                const apiLead = response.data?.lead || response.data?.data?.lead;
                
                // IMPORTANT: Use API response if available (has populated desk/agent)
                // Otherwise merge carefully to preserve populated fields
                const updatedLead = apiLead || {
                    ...lead,                    // Original lead with ALL populated fields
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    phone: formData.phone,
                    email: formData.email,
                    status: formData.status,
                    source: formData.source,
                    comments: comments,
                    _id: lead._id
                };
                
                // Update localLead immediately for instant UI feedback
                setLocalLead(updatedLead);
                
                // Call onLeadUpdate to update parent (Leads page Redux store)
                // Delay the call slightly to ensure save completes and modal stays stable
                // This updates Redux without causing modal to close
                if (onLeadUpdate) {
                    // Use requestAnimationFrame to defer the update until after the current render cycle
                    requestAnimationFrame(() => {
                        // Reset saving flag first, then update parent
                        isSavingRef.current = false;
                        onLeadUpdate(updatedLead);
                    });
                } else {
                    // If no onLeadUpdate, reset saving flag immediately
                    setTimeout(() => {
                        isSavingRef.current = false;
                    }, 100);
                }
            } else {
                toast.error("Failed to update lead");
            }
        } catch (error) {
            toast.error("Failed to update lead");
            // Reset saving flag on error
            setTimeout(() => {
                isSavingRef.current = false;
            }, 100);
        } finally {
            setLoading(false);
            // Note: Saving flag is reset in success path via requestAnimationFrame
            // This ensures parent updates happen after save completes without closing modal
        }
    };

    // Refresh lead data after callback operations
    const refreshLeadData = async () => {
        if (!localLead?._id) return;
        
        try {
            const response = await getLeadDataById(localLead._id);
            if (response?.data?._id) {
                // Update local state immediately for instant UI update
                setLocalLead(response.data);
                
                // Update comments with sorted, properly structured data
                if (response.data.comments && response.data.comments.length) {
                    const sortedComments = response.data.comments
                        .map(comment => ({
                            ...comment,
                            id: comment._id || comment.id || Math.random().toString(36).substr(2, 9),
                            isSystem: comment.isSystem || false
                        }))
                        .sort((a, b) => {
                            const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                            const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                            return dateB - dateA; // Descending order (newest first)
                        });
                    setComments(sortedComments);
                }
                
                // Also update parent component with fresh data
                if (onLeadUpdate) {
                    onLeadUpdate(response.data);
                }
            }
        } catch (error) {
            console.error('Error refreshing lead data:', error);
        }
    };

    // Callback handlers
    const handleScheduleCallback = () => {
        setCallbackMode("schedule");
        setShowCallbackDialog(true);
    };

    const handleRescheduleCallback = () => {
        setCallbackMode("reschedule");
        setShowCallbackDialog(true);
    };

    const handleCallbackSuccess = async (callbackData) => {
        if (!localLead?._id) return;
        
        try {
            let response;
            if (callbackMode === "reschedule") {
                response = await rescheduleCallback(localLead._id, callbackData);
            } else {
                response = await scheduleCallback(localLead._id, callbackData);
            }

            if (response.status === 200) {
                toast.success(callbackMode === "reschedule" ? "Callback rescheduled!" : "Callback scheduled!");
                setShowCallbackDialog(false);
                
                // Refresh lead data to show updated callback
                await refreshLeadData();
            } else {
                toast.error(response.message || "Failed to schedule callback");
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.message || "Error scheduling callback";
            toast.error(errorMsg);
        }
    };

    const handleCompleteCallback = async () => {
        if (!localLead?._id) return;
        
        try {
            const response = await completeCallback(localLead._id);
            if (response.status === 200) {
                toast.success("Callback marked as completed!");
                // Refresh lead data to show updated callback
                await refreshLeadData();
            } else {
                toast.error(response.message || "Failed to complete callback");
            }
        } catch (error) {
            toast.error("Error completing callback");
        }
    };

    const handleCancelCallback = async () => {
        if (!localLead?._id) return;
        
        if (window.confirm("Are you sure you want to cancel this callback?")) {
            try {
                const response = await cancelCallback(localLead._id);
                if (response.status === 200) {
                    toast.success("Callback cancelled!");
                    // Refresh lead data to show updated callback
                    await refreshLeadData();
                } else {
                    toast.error(response.message || "Failed to cancel callback");
                }
            } catch (error) {
                toast.error("Error cancelling callback");
            }
        }
    };

    const handleTransfer = () => {
        setShowTransferModal(true);
    };

    const handleTransferSuccess = () => {
        setShowTransferModal(false);
        onClose();
        // Refresh the leads list or navigate away
        if (onSuccess) {
            onSuccess();
        }
    };

    const handleScreenshotUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // Validate file types
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
        const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
        if (invalidFiles.length > 0) {
            toast.error("Only JPG, PNG, GIF, and PDF files are allowed");
            return;
        }

        // Validate file sizes (10MB max)
        const maxSize = 10 * 1024 * 1024; // 10MB
        const oversizedFiles = files.filter(file => file.size > maxSize);
        if (oversizedFiles.length > 0) {
            toast.error("File size must be less than 10MB");
            return;
        }

        setUploadingScreenshots(true);
        try {
            const response = await uploadScreenshots(lead._id, files);
            if (response?.status === 200) {
                toast.success(`Successfully uploaded ${files.length} screenshot(s)`);
                // Update screenshots list
                setScreenshots(prev => [...prev, ...response.data.screenshots]);
                // Update lead in parent component
                if (onLeadUpdate) {
                    const updatedLead = { ...lead, screenshots: [...screenshots, ...response.data.screenshots] };
                    onLeadUpdate(updatedLead);
                }
                // Clear file input
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            } else {
                toast.error(response?.message || "Failed to upload screenshots");
            }
        } catch (error) {
            console.error("Error uploading screenshots:", error);
            const errorMessage = error?.response?.data?.message || 
                                error?.message || 
                                error?.response?.data?.error ||
                                "Failed to upload screenshots";
            toast.error(errorMessage);
        } finally {
            setUploadingScreenshots(false);
        }
    };

    const handleDeleteScreenshot = async (screenshotId) => {
        if (!window.confirm("Are you sure you want to delete this screenshot?")) {
            return;
        }

        try {
            const response = await deleteScreenshot(lead._id, screenshotId);
            if (response?.status === 200) {
                toast.success("Screenshot deleted successfully");
                // Remove from screenshots list
                setScreenshots(prev => prev.filter(s => s._id !== screenshotId));
                // Update lead in parent component
                if (onLeadUpdate) {
                    const updatedLead = { ...lead, screenshots: screenshots.filter(s => s._id !== screenshotId) };
                    onLeadUpdate(updatedLead);
                }
            } else {
                toast.error(response?.message || "Failed to delete screenshot");
            }
        } catch (error) {
            console.error("Error deleting screenshot:", error);
            toast.error("Failed to delete screenshot");
        }
    };

    const getScreenshotUrl = (screenshot) => {
        // Construct URL to access the screenshot
        // The backend serves static files from /lead-screenshots route
        const baseUrl = serviceConstants.API_HOST_NAME.replace(/\/$/, ''); // Remove trailing slash
        const imagePath = screenshot.path.startsWith('/') ? screenshot.path : `/${screenshot.path}`;
        return `${baseUrl}${imagePath}`;
    };

    // Keyboard navigation - arrow keys to navigate between leads
    // MUST be before early return to follow Rules of Hooks
    useEffect(() => {
        if (!open || !hasNavigation || !lead) {
            return;
        }

        const handleKeyDown = (e) => {
            // Don't trigger if user is typing in an input, textarea, or select
            const isInputFocused = e.target.tagName === 'INPUT' || 
                                  e.target.tagName === 'TEXTAREA' || 
                                  e.target.tagName === 'SELECT' ||
                                  e.target.isContentEditable;
            
            if (isInputFocused || navigationLoading || loading) {
                return;
            }

            // Left arrow - previous lead
            if (e.key === 'ArrowLeft' && canGoPrevious) {
                e.preventDefault();
                handlePrevious();
            }
            
            // Right arrow - next lead
            if (e.key === 'ArrowRight' && canGoNext) {
                e.preventDefault();
                handleNext();
            }
        };

        // Add event listener
        window.addEventListener('keydown', handleKeyDown);

        // Cleanup
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [open, hasNavigation, canGoPrevious, canGoNext, loading, navigationLoading, handlePrevious, handleNext, lead]);

    // Early return AFTER all hooks
    if (!lead) return null;

    const voipProviders = getVoipProviders();

    return (
        <Dialog open={open} onOpenChange={handleDialogOpenChange}>
            <DialogContent className="max-w-[95vw] max-h-[95vh] overflow-y-auto">
                <DialogHeader>
                    <div className="flex items-center justify-between">
                        <DialogTitle className="flex items-center text-xl">
                            <PhoneCall className="h-5 w-5 mr-2 text-blue-600" />
                            Call Lead - {getFullName(lead)}
                        </DialogTitle>
                        
                        {/* Navigation Controls */}
                        {hasNavigation && (
                            <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-600 font-medium mr-2">
                                    {leadPosition}
                                </span>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={handlePrevious}
                                    disabled={!canGoPrevious || loading || navigationLoading}
                                    title="Previous Lead"
                                >
                                    {navigationLoading ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                                    ) : (
                                        <ChevronLeft className="h-4 w-4" />
                                    )}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={handleNext}
                                    disabled={!canGoNext || loading || navigationLoading}
                                    title="Next Lead"
                                >
                                    {navigationLoading ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                                    ) : (
                                        <ChevronRight className="h-4 w-4" />
                                    )}
                                </Button>
                            </div>
                        )}
                    </div>
                </DialogHeader>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                    {/* Left Column - Lead Info & VOIP */}
                    <div className="space-y-6">
                        {/* Lead Information */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center text-lg">
                                    <User className="h-5 w-5 mr-2 text-gray-600" />
                                    Lead Information
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="firstName">First Name</Label>
                                        <Input
                                            id="firstName"
                                            value={formData.firstName}
                                            onChange={(e) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    firstName: e.target.value
                                                }))
                                            }
                                            disabled={!user.permissions.editLeadFields?.firstName}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="lastName">Last Name</Label>
                                        <Input
                                            id="lastName"
                                            value={formData.lastName}
                                            onChange={(e) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    lastName: e.target.value
                                                }))
                                            }
                                            disabled={!user.permissions.editLeadFields?.lastName}
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    {user.permissions.leads.viewPhone && (
                                        <div>
                                            <Label htmlFor="phone">Phone Number</Label>
                                            <Input
                                                id="phone"
                                                value={formData.phone}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        phone: e.target.value
                                                    }))
                                                }
                                                disabled={
                                                    !user.permissions.leads.viewPhone ||
                                                    !user.permissions.editLeadFields?.phone
                                                }
                                                className="font-mono"
                                            />
                                        </div>
                                    )}

                                    {user.permissions.leads.viewEmail && (
                                        <div>
                                            <Label htmlFor="email">Email</Label>
                                            <div className="flex space-x-2">
                                                <Input
                                                    id="email"
                                                    type="email"
                                                    value={formData.email}
                                                    onChange={(e) =>
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            email: e.target.value
                                                        }))
                                                    }
                                                    disabled={!user.permissions.editLeadFields?.email}
                                                    className="flex-1"
                                                />
                                                {formData.email && (
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="icon"
                                                        onClick={async () => {
                                                            try {
                                                                await navigator.clipboard.writeText(formData.email);
                                                                setEmailCopied(true);
                                                                toast.success("Email copied to clipboard!");
                                                                setTimeout(() => setEmailCopied(false), 2000);
                                                            } catch (error) {
                                                                console.error("Failed to copy email:", error);
                                                                toast.error("Failed to copy email");
                                                            }
                                                        }}
                                                        className="shrink-0"
                                                        title="Copy email"
                                                    >
                                                        {emailCopied ? (
                                                            <Check className="h-4 w-4 text-green-600" />
                                                        ) : (
                                                            <Copy className="h-4 w-4" />
                                                        )}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <div>
                                        <Label htmlFor="status">Status</Label>
                                        <Select
                                            options={leadStatuses}
                                            value={formData.status}
                                            onChange={(value) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    status: value
                                                }))
                                            }
                                            disabled={!user.permissions.editLeadFields?.status}
                                            searchable={true}
                                            searchPlaceholder="Search statuses..."
                                        />
                                        
                                        {/* Keyboard Shortcuts Legend */}
                                        {leadStatuses.filter(s => s.keyboardShortcut).length > 0 && (
                                            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                                                <p className="text-xs font-semibold text-blue-700 mb-1">Quick Shortcuts:</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {leadStatuses
                                                        .filter(s => s.keyboardShortcut)
                                                        .map((status) => (
                                                            <div
                                                                key={status.value}
                                                                className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-white rounded border border-blue-300"
                                                                title={`Press ${status.keyboardShortcut.toUpperCase()} for ${status.label}`}
                                                            >
                                                                <kbd className="px-1 py-0.5 text-xs font-bold text-gray-700 bg-gray-100 border border-gray-300 rounded">
                                                                    {status.keyboardShortcut.toUpperCase()}
                                                                </kbd>
                                                                <span className="text-xs text-gray-600">=</span>
                                                                <span 
                                                                    className="text-xs font-medium px-1 py-0.5 rounded text-white"
                                                                    style={{ backgroundColor: status.color || '#3B82F6' }}
                                                                >
                                                                    {status.label}
                                                                </span>
                                                            </div>
                                                        ))
                                                    }
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <Label htmlFor="source">Source</Label>
                                        <Select
                                            options={sourceOptions}
                                            value={formData.source}
                                            onChange={(value) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    source: value || ""
                                                }))
                                            }
                                            disabled={!user.permissions.editLeadFields?.source}
                                            searchable={true}
                                            searchPlaceholder="Search sources..."
                                            placeholder="Select source"
                                        />
                                    </div>
                                </div>
                                
                                {/* Country & Local Time Display */}
                                {lead.country && (
                                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-2">
                                                <MapPin className="h-4 w-4 text-blue-600" />
                                                <span className="text-sm font-medium text-blue-700">Country:</span>
                                                <span className="text-sm text-blue-900 font-semibold">{lead.country}</span>
                                            </div>
                                            {localTime && (
                                                <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-md border border-blue-300">
                                                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                                    <span className="text-xs font-mono font-semibold text-gray-900">{localTime}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Callback Schedule Display */}
                                {localLead && localLead.callbackSchedule && localLead.callbackSchedule.scheduledFor && !localLead.callbackSchedule.completed && (
                                    <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start gap-2">
                                                <Calendar className="h-4 w-4 text-yellow-600 mt-0.5" />
                                                <div>
                                                    <p className="text-sm font-medium text-yellow-900">
                                                        Callback Scheduled
                                                    </p>
                                                    <p className="text-sm text-yellow-700">
                                                        {moment(localLead.callbackSchedule.scheduledFor).format("MMM D, YYYY h:mm A")}
                                                        <span className="ml-2 text-yellow-600">
                                                            ({moment(localLead.callbackSchedule.scheduledFor).fromNow()})
                                                        </span>
                                                    </p>
                                                    {localLead.callbackSchedule.note && (
                                                        <p className="text-xs text-yellow-600 mt-1">
                                                            Note: {localLead.callbackSchedule.note}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex gap-1">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={handleRescheduleCallback}
                                                    className="text-xs"
                                                >
                                                    <Clock className="h-3 w-3 mr-1" />
                                                    Reschedule
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={handleCompleteCallback}
                                                    className="text-xs text-green-600 hover:text-green-700"
                                                >
                                                    <Check className="h-3 w-3 mr-1" />
                                                    Complete
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={handleCancelCallback}
                                                    className="text-xs text-red-600 hover:text-red-700"
                                                >
                                                    <X className="h-3 w-3 mr-1" />
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Callback History */}
                                {localLead && localLead.callbackHistory && localLead.callbackHistory.length > 0 && (
                                    <div className="mt-4 space-y-2">
                                        <details className="group">
                                            <summary className="cursor-pointer list-none flex items-center justify-between p-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
                                                <div className="flex items-center gap-2">
                                                    <History className="h-4 w-4 text-gray-600" />
                                                    <span className="text-sm font-medium text-gray-700">
                                                        Callback History ({localLead.callbackHistory.length})
                                                    </span>
                                                </div>
                                                <ChevronRight className="h-4 w-4 text-gray-500 group-open:rotate-90 transition-transform" />
                                            </summary>
                                            <div className="mt-2 space-y-2">
                                                {localLead.callbackHistory.slice().reverse().map((historyItem, index) => (
                                                    <div 
                                                        key={index} 
                                                        className={`p-3 rounded-lg border ${
                                                            historyItem.completed 
                                                                ? 'bg-green-50 border-green-200' 
                                                                : 'bg-gray-50 border-gray-200'
                                                        }`}
                                                    >
                                                        <div className="flex items-start justify-between">
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    {historyItem.completed ? (
                                                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                                                    ) : (
                                                                        <XCircle className="h-4 w-4 text-gray-400" />
                                                                    )}
                                                                    <span className="text-xs font-medium text-gray-900">
                                                                        {moment(historyItem.scheduledFor).format("MMM D, YYYY h:mm A")}
                                                                    </span>
                                                                </div>
                                                                {historyItem.note && (
                                                                    <p className="text-xs text-gray-600 ml-6">
                                                                        {historyItem.note}
                                                                    </p>
                                                                )}
                                                                {historyItem.completed && historyItem.completedAt && (
                                                                    <p className="text-xs text-green-600 ml-6 mt-1">
                                                                        Completed: {moment(historyItem.completedAt).format("MMM D, YYYY h:mm A")}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </details>
                                    </div>
                                )}

                                <div className="flex justify-end space-x-2 mt-4">
                                    {/* Schedule Callback Button */}
                                    {localLead && (!localLead.callbackSchedule || !localLead.callbackSchedule.scheduledFor || localLead.callbackSchedule.completed) && (
                                        <Button
                                            type="button"
                                            onClick={handleScheduleCallback}
                                            disabled={loading}
                                            className=""
                                        >
                                            <Calendar className="h-4 w-4 mr-2" />
                                            Schedule Callback
                                        </Button>
                                    )}
                                    
                                    <Button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleSave();
                                        }}
                                        disabled={loading}
                                        className=""
                                    >
                                        <Save className="h-4 w-4 mr-2" />
                                        {loading ? "Saving..." : "Save Lead"}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        {/* VOIP Providers */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center text-lg">
                                    <Settings className="h-5 w-5 mr-2 text-gray-600" />
                                    Call Providers
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {activeCall && (
                                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center space-x-2">
                                                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                                                <span className="font-medium text-green-800">
                                                    {activeCall.status === "initiating" ? "Initiating Call..." : "Call Active"}
                                                </span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                {activeCall.provider !== "commpeak" && activeCall.provider !== "globalcall" && activeCall.status !== "initiating" && (
                                                    <Button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleEndCall();
                                                        }}
                                                        variant="destructive"
                                                        size="sm"
                                                        disabled={endingCall}
                                                        className="text-white"
                                                    >
                                                        {endingCall ? (
                                                            <>
                                                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                                                Ending...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <PhoneOff className="h-4 w-4 mr-1" />
                                                                End Call
                                                            </>
                                                        )}
                                                    </Button>
                                                )}
                                                <Button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleRefreshCall();
                                                    }}
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-green-700 hover:text-green-800 hover:bg-green-100"
                                                    title="Clear call state"
                                                >
                                                    <RefreshCw className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                        <p className="text-sm text-green-600">
                                            Provider: {activeCall.provider.toUpperCase()}
                                            {activeCall.startTime && ` | Started: ${format(activeCall.startTime, "HH:mm:ss")}`}
                                        </p>
                                        {activeCall.extension && (
                                            <p className="text-xs text-green-500 mt-1">
                                                Extension: {activeCall.extension}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {voipProviders.length > 0 ? (
                                    <div className="space-y-3">
                                        {voipProviders.map((provider, index) => {
                                            const IconComponent = provider.icon;
                                            const isActiveProvider = selectedProvider === provider.id;
                                            return (
                                                <Button
                                                    key={provider.id + index}
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleCall(provider);
                                                    }}
                                                    className={`w-full ${provider.color} text-white relative`}
                                                    disabled={
                                                        !formData.phone ||
                                                        loading ||
                                                        activeCall
                                                    }
                                                >
                                                    <IconComponent className="h-4 w-4 mr-2" />
                                                    {loading && isActiveProvider ? (
                                                        <>
                                                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                                            Connecting...
                                                        </>
                                                    ) : (
                                                        `Call via ${provider.name}`
                                                    )}
                                                </Button>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center py-4 text-gray-500">
                                        <PhoneCall className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                        <p>No VOIP providers configured</p>
                                        <p className="text-sm">Contact admin to set up call providers</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right Column - Comments */}
                    <div className="space-y-6">
                        <Card className="flex flex-col">
                            <CardHeader className="pb-3">
                                <CardTitle className="flex items-center text-lg">
                                    <MessageCircle className="h-5 w-5 mr-2 text-green-600" />
                                    Comments ({comments.length + (lead?.lastComment ? 1 : 0)})
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="flex-1 flex flex-col p-0">
                                {/* Add Comment Section */}
                                <div className="p-4 border-b">
                                    <div className="flex gap-2">
                                        <Textarea
                                            placeholder="Add a comment..."
                                            value={newComment}
                                            onChange={(e) => setNewComment(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            rows={2}
                                            className="flex-1 resize-none"
                                        />
                                        <Button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleAddComment();
                                            }}
                                            disabled={!newComment.trim()}
                                            size="sm"
                                            className="self-end"
                                        >
                                            <Send className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Press Enter to send, Shift+Enter for new line
                                    </p>
                                </div>

                                {/* Comments List */}
                                <div className="flex-1 overflow-y-auto p-4">
                                    {comments.length === 0 && !lead?.lastComment ? (
                                        <div className="text-center text-gray-500 py-8">
                                            <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                            <p>No comments yet</p>
                                            <p className="text-sm">Add the first comment above</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-4 max-h-[300px] overflow-y-auto">
                                            {/* Display lastComment if it exists */}
                                            {lead?.lastComment && (
                                                <div className="p-3 rounded-lg bg-yellow-50 border-l-4 border-yellow-400">
                                                    <div className="flex justify-between items-start mb-2 gap-2">
                                                        <div className="flex flex-col">
                                                            <span className="text-sm font-medium text-yellow-800">
                                                                Last Comment
                                                            </span>
                                                            {lead.lastCommStaff && (
                                                                <span className="text-xs text-yellow-700 mt-0.5">
                                                                    by {getFullName(lead.lastCommStaff)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            <span className="text-xs text-gray-500">
                                                                {lead.lastCommDate 
                                                                    ? format(new Date(lead.lastCommDate), "MMM dd, HH:mm")
                                                                    : (lead.updatedAt ? format(new Date(lead.updatedAt), "MMM dd, HH:mm") : "")
                                                                }
                                                            </span>
                                                            {user.position.role === "admin" && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={handleDeleteLastComment}
                                                                    className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                    title="Delete comment"
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                                        {lead.lastComment}
                                                    </p>
                                                </div>
                                            )}
                                            {comments.map((comment) => {
                                                const commentId = comment._id || comment.id;
                                                return (
                                                    <div
                                                        key={comment.id}
                                                        className={`p-3 rounded-lg ${
                                                            comment.isSystem
                                                                ? "bg-blue-50 border-l-4 border-blue-400"
                                                                : "bg-gray-50"
                                                        }`}
                                                    >
                                                        <div className="flex justify-between items-start mb-2">
                                                            <span
                                                                className={`text-sm font-medium ${
                                                                    comment.isSystem ? "text-blue-700" : "text-gray-900"
                                                                }`}
                                                            >
                                                                {comment.affiliateName 
                                                                    ? `Comment by ${comment.affiliateName}`
                                                                    : comment.author
                                                                        ? getFullName(comment.author)
                                                                        : "Unknown User"}
                                                            </span>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-xs text-gray-500">
                                                                    {format(new Date(comment.createdAt), "MMM dd, HH:mm")}
                                                                </span>
                                                                {user.position.role === "admin" && (
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => handleDeleteComment(commentId)}
                                                                        className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                        title="Delete comment"
                                                                    >
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                                            {comment.text}
                                                        </p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Screenshots Section */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center justify-between text-lg">
                                    <div className="flex items-center">
                                        <Image className="h-5 w-5 mr-2 text-purple-600" />
                                        Screenshots {screenshots.length > 0 && `(${screenshots.length})`}
                                    </div>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploadingScreenshots}
                                        className="text-xs"
                                    >
                                        <Upload className="h-3 w-3 mr-1" />
                                        Upload
                                    </Button>
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    accept="image/jpeg,image/jpg,image/png,image/gif,application/pdf"
                                    onChange={handleScreenshotUpload}
                                    className="hidden"
                                />
                                
                                {uploadingScreenshots && (
                                    <div className="text-center py-4">
                                        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-purple-600" />
                                        <p className="text-sm text-gray-600">Uploading screenshots...</p>
                                    </div>
                                )}

                                {screenshots.length === 0 && !uploadingScreenshots ? (
                                    <div className="text-center text-gray-500 py-8">
                                        <Image className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                        <p className="text-sm">No screenshots yet</p>
                                        <p className="text-xs text-gray-400 mt-1">Click Upload to add screenshots</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-3 gap-3">
                                        {screenshots.map((screenshot) => {
                                            const isImage = screenshot.filename.match(/\.(jpg|jpeg|png|gif)$/i);
                                            const isPdf = screenshot.filename.match(/\.pdf$/i);
                                            
                                            return (
                                                <div
                                                    key={screenshot._id}
                                                    className="relative group cursor-pointer"
                                                    onClick={() => setSelectedImage(screenshot)}
                                                >
                                                    {isImage ? (
                                                        <img
                                                            src={getScreenshotUrl(screenshot)}
                                                            alt={screenshot.originalName}
                                                            className="w-full h-24 object-cover rounded-lg border border-gray-200 hover:border-purple-400 transition-colors"
                                                            onError={(e) => {
                                                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect width="100" height="100" fill="%23f3f4f6"/%3E%3Ctext x="50" y="50" text-anchor="middle" fill="%239ca3af"%3EImage%3C/text%3E%3C/svg%3E';
                                                            }}
                                                        />
                                                    ) : isPdf ? (
                                                        <div className="w-full h-24 bg-red-50 border border-red-200 rounded-lg flex items-center justify-center">
                                                            <FileText className="h-8 w-8 text-red-500" />
                                                        </div>
                                                    ) : (
                                                        <div className="w-full h-24 bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center">
                                                            <Image className="h-8 w-8 text-gray-400" />
                                                        </div>
                                                    )}
                                                    
                                                    {/* Delete button on hover - Admin only */}
                                                    {user.position.role === "admin" && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteScreenshot(screenshot._id);
                                                            }}
                                                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                                                            title="Delete screenshot"
                                                        >
                                                            <Trash2 className="h-3 w-3" />
                                                        </button>
                                                    )}
                                                    
                                                    {/* Overlay on hover */}
                                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all flex items-center justify-center">
                                                        <span className="text-white text-xs opacity-0 group-hover:opacity-100">
                                                            View
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>

                <div className="flex justify-end items-center mt-6 pt-4 border-t">
                    <div className="flex space-x-3">
                        {user.position.role === "agent" && user.position.floor === "conversion" && (
                            <Button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleTransfer();
                                }}
                                variant="outline"
                                className=""
                            >
                                <ArrowRightLeft className="h-4 w-4 mr-2" />
                                Transfer Lead
                            </Button>
                        )}
                        <Button type="button" onClick={onClose} variant="outline">
                            Close
                        </Button>
                    </div>
                </div>
            </DialogContent>

            <AgentTransferModal
                open={showTransferModal}
                onClose={() => setShowTransferModal(false)}
                onSuccess={handleTransferSuccess}
                lead={lead}
            />

            {/* Callback Schedule Dialog */}
            {localLead && (
                <CallbackScheduleDialog
                    open={showCallbackDialog}
                    onClose={() => setShowCallbackDialog(false)}
                    lead={localLead}
                    existingCallback={localLead?.callbackSchedule}
                    mode={callbackMode}
                    onSuccess={handleCallbackSuccess}
                />
            )}

            {/* Screenshot Lightbox Modal */}
            {selectedImage && (
                <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
                    <DialogContent className="max-w-4xl max-h-[90vh]">
                        <DialogHeader>
                            <DialogTitle className="flex items-center justify-between">
                                <span>{selectedImage.originalName}</span>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setSelectedImage(null)}
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </DialogTitle>
                        </DialogHeader>
                        <div className="flex items-center justify-center p-4">
                            {selectedImage.filename.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                                <img
                                    src={getScreenshotUrl(selectedImage)}
                                    alt={selectedImage.originalName}
                                    className="max-w-full max-h-[70vh] object-contain rounded-lg"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            ) : selectedImage.filename.match(/\.pdf$/i) ? (
                                <div className="w-full h-[70vh] flex items-center justify-center bg-gray-50 rounded-lg border">
                                    <div className="text-center">
                                        <FileText className="h-16 w-16 mx-auto mb-4 text-red-500" />
                                        <p className="text-gray-600 mb-4">PDF Preview not available</p>
                                        <a
                                            href={getScreenshotUrl(selectedImage)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:underline"
                                        >
                                            Open PDF in new tab
                                        </a>
                                    </div>
                                </div>
                            ) : (
                                <div className="w-full h-[70vh] flex items-center justify-center bg-gray-50 rounded-lg border">
                                    <p className="text-gray-600">Preview not available</p>
                                </div>
                            )}
                        </div>
                        <div className="flex justify-between items-center text-sm text-gray-500 pt-4 border-t">
                            <span>
                                Uploaded {selectedImage.uploadedAt ? format(new Date(selectedImage.uploadedAt), "MMM dd, yyyy HH:mm") : ""}
                            </span>
                            {user.position.role === "admin" && (
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => {
                                        handleDeleteScreenshot(selectedImage._id);
                                        setSelectedImage(null);
                                    }}
                                >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                </Button>
                            )}
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </Dialog>
    );
}
